// ============================================================================
// NewReminderDialog — modal « Nouveau rappel » (L10, F-166)
// ============================================================================
// Portage maison du PoC new-reminder-dialog.tsx : modal locale (tokens DS, gabarit
// NewPostingDialog E1d) + useCreateReminder.
// Champs PoC : titre, note (body), echeance (due_date) — 3 onChange, comme la source.
//
// NT-L10-01 (porte TEL QUEL) : `related_email` est ACCEPTE par le schema serveur
// (parite CreateReminderSchema) mais le wizard PoC ne l'expose PAS. On reproduit
// l'ecart fidelement : aucun champ UI ici, le serveur le tolere. Consigne au rapport.
// M-155 : le rappel est TOUJOURS cree au nom du viewer — le serveur n'accepte aucun
// director_email entrant, donc rien a envoyer ni a choisir ici.

import { useState } from 'react';
import { X } from 'lucide-react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { useCreateReminder } from '../../hooks/useMonEquipe';
import { teamErrorCode } from '../../hooks/useTeams';

export function NewReminderDialog({
  open,
  onClose,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  /** Remonte le code court d'erreur a la page (alerte ?error= — F-155). */
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateReminder();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [stepError, setStepError] = useState<string | null>(null);

  if (!open) return null;

  function close() {
    setTitle('');
    setBody('');
    setDueDate('');
    setStepError(null);
    onClose();
  }

  function submit() {
    if (!title.trim()) {
      setStepError(t('gest:monEquipe.reminders.form.titleRequired'));
      return;
    }
    setStepError(null);
    create.mutate(
      { title: title.trim(), body: body.trim() ? body.trim() : null, dueDate: dueDate || null },
      {
        onSuccess: () => close(),
        onError: (err) => {
          close();
          onError(teamErrorCode(err));
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('gest:monEquipe.reminders.form.title')}
    >
      <div className="flex max-h-full w-full max-w-lg flex-col gap-4 overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-md dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-body font-semibold text-slate-900 dark:text-slate-100">
            {t('gest:monEquipe.reminders.form.title')}
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label={t('gest:monEquipe.reminders.form.cancel')}
            className="touch-manipulation rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="new-reminder-title" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:monEquipe.reminders.form.reminderTitle')}
          </label>
          <input
            id="new-reminder-title"
            autoFocus
            maxLength={300}
            placeholder={t('gest:monEquipe.reminders.form.reminderTitlePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={`${GS_COMPONENTS.input}`}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="new-reminder-body" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:monEquipe.reminders.form.body')}
          </label>
          <textarea
            id="new-reminder-body"
            rows={4}
            maxLength={2000}
            placeholder={t('gest:monEquipe.reminders.form.bodyPlaceholder')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className={`${GS_COMPONENTS.input}`}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="new-reminder-due" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:monEquipe.reminders.form.dueDate')}
          </label>
          <input
            id="new-reminder-due"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className={`${GS_COMPONENTS.input} w-48`}
          />
        </div>

        {stepError ? (
          <p role="alert" className="text-field text-red-600 dark:text-red-400">{stepError}</p>
        ) : null}

        <div className="mt-1 flex items-center justify-end gap-2">
          <button type="button" onClick={close} className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}>
            {t('gest:monEquipe.reminders.form.cancel')}
          </button>
          <button type="button" onClick={submit} disabled={create.isPending} className={GS_COMPONENTS.btnAction}>
            {t('gest:monEquipe.reminders.form.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
